/**
 * Un petit moteur de correspondance pour le mode `?demo` (ARTO-130) : il
 * applique les règles à l'échantillon d'un profil de sources fictif, pour que
 * l'aperçu réagisse à chaque changement sans API. Une approximation honnête
 * de `apps/api/src/reprise/regles.ts` : mêmes transformations, mêmes refus de
 * ligne (date ambiguë, montant illisible, champ obligatoire vide), sans
 * dépliage ni jointure un-à-plusieurs. Aucune donnée réelle.
 */
import { ApiError } from './api.js';
import type {
  ApercuCorrespondance,
  BlocNature,
  ChampRegle,
  CiblePivot,
  ErreurRegle,
  ErreurSource,
  FiltreRegle,
  FormatDate,
  RefSource,
  Regles,
  SortieApercu,
  Surcharges,
  Transformation,
} from './correspondance.js';

export interface LigneEchantillon {
  ligne: number;
  valeurs: Record<string, string>;
}

export interface EntreeDemo {
  fichier: string;
  lignes: LigneEchantillon[];
}

const sansAccent = (texte: string): string =>
  texte
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

function enTitre(texte: string): string {
  return texte
    .toLocaleLowerCase('fr')
    .replace(
      /(^|[\s\-'’(])(\p{L})/gu,
      (_, avant: string, lettre: string) => avant + lettre.toLocaleUpperCase('fr'),
    );
}

function iso(annee: number, mois: number, jour: number): string | null {
  const date = new Date(Date.UTC(annee, mois - 1, jour));
  if (
    date.getUTCFullYear() !== annee ||
    date.getUTCMonth() !== mois - 1 ||
    date.getUTCDate() !== jour
  )
    return null;
  return date.toISOString().slice(0, 10);
}

const JOUR_MOIS_ANNEE = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/;

type Resultat = { valeur: string } | { erreur: string };

function lireDate(texte: string, format: FormatDate): Resultat {
  const valeur = texte.trim();
  if (valeur === '') return { valeur: '' };
  if (format === 'AAAA')
    return /^\d{4}$/.test(valeur)
      ? { valeur }
      : { erreur: `« ${valeur} » n’est pas une année (AAAA).` };
  if (/^\d{4}-\d{2}-\d{2}$/.test(valeur)) return { valeur };
  if (format === 'excel_serie') {
    const serie = Math.floor(Number(valeur.replace(',', '.')));
    if (!Number.isFinite(serie) || serie < 61)
      return { erreur: `« ${valeur} » n’est pas un numéro de série de date.` };
    return {
      valeur: new Date(Date.UTC(1899, 11, 30) + serie * 86_400_000).toISOString().slice(0, 10),
    };
  }
  const trouve = JOUR_MOIS_ANNEE.exec(valeur);
  if (!trouve) return { erreur: `« ${valeur} » n’est pas une date ${format}.` };
  const [premier, second, annee] = [Number(trouve[1]), Number(trouve[2]), Number(trouve[3])];
  const lue = format === 'JJ/MM/AAAA' ? iso(annee, second, premier) : iso(annee, premier, second);
  return lue === null ? { erreur: `La date « ${valeur} » n’existe pas.` } : { valeur: lue };
}

function estAmbigue(texte: string): boolean {
  const trouve = JOUR_MOIS_ANNEE.exec(texte.trim());
  if (!trouve) return false;
  const [a, b] = [Number(trouve[1]), Number(trouve[2])];
  return a <= 12 && b <= 12 && a !== b;
}

function lireMontant(texte: string, decimal: ',' | '.'): Resultat {
  const brut = texte.replace(/[\s\u00a0\u202f]/g, '').replace(/[^\d.,-]/g, '');
  if (brut === '')
    return texte.trim() === '' ? { valeur: '' } : { erreur: `« ${texte} » n’est pas un montant.` };
  const net = decimal === ',' ? brut.replace(/\./g, '').replace(',', '.') : brut.replace(/,/g, '');
  const nombre = Number(net);
  if (!Number.isFinite(nombre)) return { erreur: `« ${texte} » n’est pas un montant.` };
  return { valeur: nombre.toFixed(2).replace('.', ',') };
}

function extraireNombre(texte: string): string {
  const trouve = /[-+]?\d+(?:[ \u00a0\u202f]\d{3})*(?:[.,]\d+)?/.exec(texte)?.[0];
  return trouve === undefined ? '' : trouve.replace(/[ \u00a0\u202f]/g, '').replace('.', ',');
}

interface Contexte {
  principal: LigneEchantillon;
  jointes: Record<string, LigneEchantillon | undefined>;
  rolePrincipal: string;
}

function lire(reference: RefSource, contexte: Contexte): string {
  if (reference.parent) return '';
  const role = reference.role ?? contexte.rolePrincipal;
  const ligne = role === contexte.rolePrincipal ? contexte.principal : contexte.jointes[role];
  return (ligne?.valeurs[reference.champ] ?? '').trim();
}

function appliquerEtape(valeur: string, etape: Transformation, contexte: Contexte): Resultat {
  switch (etape.type) {
    case 'majuscules':
      return { valeur: valeur.toLocaleUpperCase('fr') };
    case 'minuscules':
      return { valeur: valeur.toLocaleLowerCase('fr') };
    case 'titre':
      return { valeur: enTitre(valeur) };
    case 'scinder': {
      const parties = valeur.split(etape.separateur);
      const rang = etape.index < 0 ? parties.length + etape.index : etape.index;
      return { valeur: (parties[rang] ?? '').trim() };
    }
    case 'extraire_nombre':
      return { valeur: extraireNombre(valeur) };
    case 'date':
      return lireDate(valeur, etape.format);
    case 'montant':
      return lireMontant(valeur, etape.decimal);
    case 'dictionnaire': {
      if (valeur === '') return { valeur: '' };
      const cles = new Map(
        Object.entries(etape.valeurs).map(([cle, cible]) => [sansAccent(cle), cible]),
      );
      return { valeur: cles.get(sansAccent(valeur)) ?? etape.defaut ?? valeur };
    }
    case 'constante':
      return { valeur: etape.valeur };
    case 'si_vide':
      if (valeur !== '') return { valeur };
      return { valeur: etape.valeur ?? (etape.source ? lire(etape.source, contexte) : '') };
    case 'concat': {
      const autre = lire(etape.source, contexte);
      return { valeur: [valeur, autre].filter((morceau) => morceau !== '').join(etape.separateur) };
    }
    case 'prefixe':
      return { valeur: valeur === '' ? '' : `${etape.valeur}${valeur}` };
  }
}

/** Les choix de la galerie posés sur les règles : une constante remplace, un dictionnaire complète. */
function avecSurcharges(regles: Regles, surcharges: Surcharges): Regles {
  const copie = structuredClone(regles);
  for (const [nature, bloc] of Object.entries(copie.natures)) {
    for (const [cle, champ] of Object.entries(bloc.colonnes)) {
      const reference = `${nature}.${cle}`;
      const constante = surcharges.constantes[reference];
      if (constante !== undefined) {
        bloc.colonnes[cle] = {
          sources: [],
          separateur: ' ',
          transformations: [{ type: 'constante', valeur: constante }],
        };
        continue;
      }
      const dictionnaire = surcharges.dictionnaires[reference];
      if (dictionnaire) {
        const existant = champ.transformations.find((etape) => etape.type === 'dictionnaire');
        if (existant && existant.type === 'dictionnaire') {
          existant.valeurs = { ...existant.valeurs, ...dictionnaire.valeurs };
          if (dictionnaire.defaut !== undefined) existant.defaut = dictionnaire.defaut;
        } else champ.transformations.push({ type: 'dictionnaire', ...dictionnaire });
      }
    }
  }
  return copie;
}

function garde(filtre: FiltreRegle, contexte: Contexte): boolean {
  const valeur = lire(filtre.source, contexte);
  switch (filtre.operateur) {
    case 'egal':
      return sansAccent(valeur) === sansAccent(filtre.valeur);
    case 'different':
      return sansAccent(valeur) !== sansAccent(filtre.valeur);
    case 'vide':
      return valeur === '';
    case 'non_vide':
      return valeur !== '';
  }
}

function calculer(champ: ChampRegle, contexte: Contexte): { valeur: string } | { erreur: string } {
  let valeur = champ.sources
    .map((reference) => lire(reference, contexte))
    .filter((morceau) => morceau !== '')
    .join(champ.separateur);
  const aUneDate = champ.transformations.some((etape) => etape.type === 'date');
  if (!aUneDate) {
    const ambigue = champ.sources.map((reference) => lire(reference, contexte)).find(estAmbigue);
    if (ambigue !== undefined)
      return {
        erreur: `Date ambiguë « ${ambigue} » : le jour et le mois peuvent s’inverser, déclarez son format.`,
      };
  }
  for (const etape of champ.transformations) {
    const lu = appliquerEtape(valeur, etape, contexte);
    if ('erreur' in lu) return lu;
    valeur = lu.valeur;
  }
  return { valeur };
}

/** Ce que la validation de l'API refuserait d'avance (`400 regles_invalides`). */
export function verifierReglesDemo(
  regles: Regles,
  natureDuLot: string,
  cibles: readonly CiblePivot[],
): ErreurRegle[] {
  const erreurs: ErreurRegle[] = [];
  if (!(natureDuLot in regles.natures))
    erreurs.push({
      chemin: 'natures',
      message: `Les règles doivent alimenter la nature du profil (« ${natureDuLot} »).`,
    });
  const roles = regles.sources.map((source) => source.role);
  for (const [nature, bloc] of Object.entries(regles.natures)) {
    const cible = cibles.find((candidate) => candidate.nature === nature);
    if (!cible) continue;
    const cles = new Set(cible.colonnes.map((colonne) => colonne.cle));
    for (const cle of Object.keys(bloc.colonnes))
      if (!cles.has(cle))
        erreurs.push({
          chemin: `natures.${nature}.colonnes.${cle}`,
          message: `« ${cle} » n’est pas une colonne du gabarit.`,
        });
    for (const colonne of cible.colonnes)
      if (colonne.obligatoire && !(colonne.cle in bloc.colonnes))
        erreurs.push({
          chemin: `natures.${nature}.colonnes`,
          message: `La colonne obligatoire « ${colonne.entete} » n’est pas alimentée.`,
        });
    for (const [cle, champ] of Object.entries(bloc.colonnes))
      for (const reference of champ.sources)
        if (reference.role !== null && !roles.includes(reference.role))
          erreurs.push({
            chemin: `natures.${nature}.colonnes.${cle}`,
            message: `Le rôle « ${reference.role} » n’est pas une source du profil.`,
          });
  }
  return erreurs;
}

export function reglesInvalidesDemo(champs: ErreurRegle[]): ApiError {
  return new ApiError(400, 'validation.invalide', 'Les règles de correspondance sont invalides.', {
    raison: 'regles_invalides',
    champs,
  });
}

function sortieVide(): SortieApercu {
  return { colonnes: [], lignes: [], filtres: 0, doublons: 0, vides: 0, refusees: 0 };
}

/** Applique les règles à l'échantillon : ce que `POST …/apercu` rendrait. */
export function appliquerReglesDemo(
  reglesBrutes: Regles,
  surcharges: Surcharges,
  entrees: Record<string, EntreeDemo | undefined>,
  cibles: readonly CiblePivot[],
): ApercuCorrespondance {
  const regles = avecSurcharges(reglesBrutes, surcharges);
  const rolePrincipal = regles.sources[0]?.role ?? 'principal';
  const principal = entrees[rolePrincipal];
  const natures: Record<string, SortieApercu> = {};
  const erreurs: ErreurSource[] = [];
  const avertissements: ErreurSource[] = [];
  let filtres = 0;

  // Index des sources jointes, par la valeur de la clé.
  const index = new Map<string, Map<string, LigneEchantillon>>();
  for (const jointure of regles.jointures) {
    const lignes = new Map<string, LigneEchantillon>();
    for (const ligne of entrees[jointure.role]?.lignes ?? [])
      lignes.set((ligne.valeurs[jointure.cle] ?? '').trim(), ligne);
    index.set(jointure.role, lignes);
  }

  for (const nature of Object.keys(regles.natures)) natures[nature] = sortieVide();
  const dejaVus = new Map<string, Set<string>>();

  for (const ligne of principal?.lignes ?? []) {
    const jointes: Contexte['jointes'] = {};
    for (const jointure of regles.jointures) {
      jointes[jointure.role] = index
        .get(jointure.role)
        ?.get((ligne.valeurs[jointure.sur] ?? '').trim());
      if (jointes[jointure.role] === undefined)
        avertissements.push({
          fichier: principal?.fichier ?? '',
          ligne: ligne.ligne,
          nature: null,
          colonne: null,
          message: `Aucune ligne de « ${jointure.role} » ne correspond à la clé.`,
        });
    }
    const contexte: Contexte = { principal: ligne, jointes, rolePrincipal };
    if (!regles.filtres.every((filtre) => garde(filtre, contexte))) {
      filtres += 1;
      continue;
    }
    for (const [nature, bloc] of Object.entries(regles.natures) as [string, BlocNature][]) {
      const sortie = natures[nature]!;
      if (!bloc.filtres.every((filtre) => garde(filtre, contexte))) {
        sortie.filtres += 1;
        continue;
      }
      const cible = cibles.find((candidate) => candidate.nature === nature);
      const enregistrement: Record<string, string> = {};
      let refusee = false;
      for (const [cle, champ] of Object.entries(bloc.colonnes)) {
        const resultat = calculer(champ, contexte);
        const entete = cible?.colonnes.find((colonne) => colonne.cle === cle)?.entete ?? cle;
        if ('erreur' in resultat) {
          erreurs.push({
            fichier: principal?.fichier ?? '',
            ligne: ligne.ligne,
            nature,
            colonne: entete,
            message: resultat.erreur,
          });
          refusee = true;
          continue;
        }
        enregistrement[cle] = resultat.valeur;
      }
      if (bloc.cleOrigine && cible?.colonnes.some((colonne) => colonne.cle === 'refOrigine'))
        enregistrement['refOrigine'] = lire(bloc.cleOrigine, contexte);
      for (const colonne of cible?.colonnes ?? [])
        if (colonne.obligatoire && !refusee && (enregistrement[colonne.cle] ?? '') === '') {
          erreurs.push({
            fichier: principal?.fichier ?? '',
            ligne: ligne.ligne,
            nature,
            colonne: colonne.entete,
            message: `La colonne « ${colonne.entete} » est obligatoire.`,
          });
          refusee = true;
        }
      if (refusee) {
        sortie.refusees += 1;
        continue;
      }
      if (Object.values(enregistrement).every((valeur) => valeur === '')) {
        sortie.vides += 1;
        continue;
      }
      if (bloc.dedoublonner !== null) {
        const vus = dejaVus.get(nature) ?? new Set<string>();
        dejaVus.set(nature, vus);
        const cle = sansAccent(enregistrement[bloc.dedoublonner] ?? '');
        if (vus.has(cle)) {
          sortie.doublons += 1;
          continue;
        }
        vus.add(cle);
      }
      sortie.lignes.push(enregistrement);
    }
  }

  for (const [nature, sortie] of Object.entries(natures)) {
    const cible = cibles.find((candidate) => candidate.nature === nature);
    sortie.colonnes = (cible?.colonnes ?? [])
      .filter((colonne) => sortie.lignes.some((ligne) => colonne.cle in ligne))
      .map(({ cle, entete }) => ({ cle, entete }));
  }
  return {
    echantillon: true,
    lus: principal?.lignes.length ?? 0,
    lignes: principal?.lignes.length ?? 0,
    filtres,
    natures,
    erreurs,
    avertissements,
  };
}
